type CreateEnvOptions = Parameters<typeof createEnv>[0];

import type { createEnv } from "@t3-oss/env-nextjs";

import { type ZodError, z } from "zod";

const formatZodEnvError = (error: ZodError) => {
  // fieldErrors: Record<string, string[]>
  const { fieldErrors } = error.flatten();

  const lines = Object.entries(fieldErrors)
    .map(([key, msgs]) => {
      // msgs is usually ['Required'] or similar
      const reason = msgs?.filter(Boolean).join(", ");
      // Show the actual (missing) runtime value as a bonus
      const received =
        process.env[key] === undefined ? "undefined" : `'${process.env[key]}'`;
      return `  • ${key} → ${reason} (received ${received})`;
    })
    .join("\n");

  return `❌ Missing or invalid environment variables:\n${lines}`;
};

export const getEnvOptions = () =>
  ({
    /**
     * Specify your client-side environment variables schema here.
     * For them to be exposed to the client, prefix them with `NEXT_PUBLIC_`.
     */
    client: {
      NEXT_PUBLIC_ADDRESS_API_URL: z.string().url().optional(),
      NEXT_PUBLIC_C15T_URL: z.string().url().optional(),
      NEXT_PUBLIC_ENVIRONMENT: z
        .enum(["local", "develop", "qat", "uat", "production", "test"])
        .default("local"),
      NEXT_PUBLIC_FROM_NUMBER: z.string().min(1),
      NEXT_PUBLIC_NOVU_API_URL: z.string().url().min(1),
      NEXT_PUBLIC_NOVU_APPLICATION_IDENTIFIER: z.string().min(1),
      NEXT_PUBLIC_NOVU_SOCKET_URL: z.string().url().min(1),
      NEXT_PUBLIC_POSTHOG_HOST: z.string().url(),
      NEXT_PUBLIC_POSTHOG_KEY: z.string().min(1),
      NEXT_PUBLIC_QUOTE_APP_URL: z.string().url(),
      NEXT_PUBLIC_SIGNABLE_WIDGET_URL_HASH: z.string().min(1),
      NEXT_PUBLIC_SITE_URL: z.string().url().optional(),
      NEXT_PUBLIC_SOUTH_AFRICA_EON_DFV_HUNT_GROUP: z.string().min(1),
      NEXT_PUBLIC_SOUTH_AFRICA_EON_HUNT_GROUP: z.string().min(1),
      NEXT_PUBLIC_SOUTH_AFRICA_OPUS_HUNT_GROUP: z.string().min(1),
      NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY: z.string().min(1),
      NEXT_PUBLIC_SUPABASE_URL: z.string().url(),
      /** Cloudflare Turnstile sitekey (public). */
      NEXT_PUBLIC_TURNSTILE_SITE_KEY: z.string().min(1)
    },
    onInvalidAccess: (variable: string) => {
      throw new Error(
        `❌ Attempted to access a server-side environment variable ${variable} on the client`
      );
    },
    onValidationError: (error: ZodError) => {
      console.error(
        "Invalid environment variables:",
        error.flatten().fieldErrors
      );
      throw new Error(formatZodEnvError(error));
    },
    /*
     * Due to how Next.js bundles environment variables on Edge and Client,
     * we need to manually destructure them all to make sure all are included in bundle.
     *
     * 💡 You'll get type errors if not all variables from `server` & `client` are included here.
     */
    runtimeEnv: {
      APERTURE_API_KEY: process.env.APERTURE_API_KEY,
      BRITISH_GAS_CLIENT_ID: process.env.BRITISH_GAS_CLIENT_ID,
      COMPANIES_HOUSE_PRIVATE_API_KEY:
        process.env.COMPANIES_HOUSE_PRIVATE_API_KEY,
      CRM_PRIVATE_API_KEY: process.env.CRM_PRIVATE_API_KEY,
      DATA8_PRIVATE_API_KEY: process.env.DATA8_PRIVATE_API_KEY,
      DATABASE_URL: process.env.DATABASE_URL,
      DEV_EMAIL_WATCHERS: process.env.DEV_EMAIL_WATCHERS,
      DIRECT_URL: process.env.DIRECT_URL,
      ELECTRALINKS_API_KEY: process.env.ELECTRALINKS_API_KEY,
      ELECTRALINKS_API_PASSWORD: process.env.ELECTRALINKS_API_PASSWORD,
      ELECTRALINKS_HH_API_KEY: process.env.ELECTRALINKS_HH_API_KEY,
      ELECTRALINKS_HH_API_PASSWORD: process.env.ELECTRALINKS_HH_API_PASSWORD,
      ENABLE_SOURCE_MAPS: process.env.ENABLE_SOURCE_MAPS,
      ENABLE_STAGEWISE_TOOLBAR: process.env.ENABLE_STAGEWISE_TOOLBAR,
      EXPERIAN_CLIENT_ID: process.env.EXPERIAN_CLIENT_ID,
      EXPERIAN_CLIENT_SECRET: process.env.EXPERIAN_CLIENT_SECRET,
      EXPERIAN_PASSWORD: process.env.EXPERIAN_PASSWORD,
      EXPERIAN_USERNAME: process.env.EXPERIAN_USERNAME,
      INNGEST_EVENT_KEY: process.env.INNGEST_EVENT_KEY,
      INNGEST_SIGNING_KEY: process.env.INNGEST_SIGNING_KEY,
      INTERNAL_RECIPIENT_EMAIL: process.env.INTERNAL_RECIPIENT_EMAIL,
      LEGACY_MYSQL_URL: process.env.LEGACY_MYSQL_URL,
      NEXT_PUBLIC_ADDRESS_API_URL: process.env.NEXT_PUBLIC_ADDRESS_API_URL,
      NEXT_PUBLIC_C15T_URL: process.env.NEXT_PUBLIC_C15T_URL,
      NEXT_PUBLIC_DISABLE_POSTHOG: process.env.NEXT_PUBLIC_DISABLE_POSTHOG,
      NEXT_PUBLIC_ENVIRONMENT: process.env.NEXT_PUBLIC_ENVIRONMENT,
      NEXT_PUBLIC_FROM_NUMBER: process.env.NEXT_PUBLIC_FROM_NUMBER,
      NEXT_PUBLIC_NOVU_API_URL: process.env.NEXT_PUBLIC_NOVU_API_URL,
      NEXT_PUBLIC_NOVU_APPLICATION_IDENTIFIER:
        process.env.NEXT_PUBLIC_NOVU_APPLICATION_IDENTIFIER,
      NEXT_PUBLIC_NOVU_SOCKET_URL: process.env.NEXT_PUBLIC_NOVU_SOCKET_URL,
      NEXT_PUBLIC_POSTHOG_HOST: process.env.NEXT_PUBLIC_POSTHOG_HOST,
      NEXT_PUBLIC_POSTHOG_KEY: process.env.NEXT_PUBLIC_POSTHOG_KEY,
      NEXT_PUBLIC_QUOTE_APP_URL: process.env.NEXT_PUBLIC_QUOTE_APP_URL,
      NEXT_PUBLIC_SIGNABLE_WIDGET_URL_HASH:
        process.env.NEXT_PUBLIC_SIGNABLE_WIDGET_URL_HASH,
      NEXT_PUBLIC_SITE_URL: process.env.NEXT_PUBLIC_SITE_URL,
      NEXT_PUBLIC_SOUTH_AFRICA_EON_DFV_HUNT_GROUP:
        process.env.NEXT_PUBLIC_SOUTH_AFRICA_EON_DFV_HUNT_GROUP,
      NEXT_PUBLIC_SOUTH_AFRICA_EON_HUNT_GROUP:
        process.env.NEXT_PUBLIC_SOUTH_AFRICA_EON_HUNT_GROUP,
      NEXT_PUBLIC_SOUTH_AFRICA_OPUS_HUNT_GROUP:
        process.env.NEXT_PUBLIC_SOUTH_AFRICA_OPUS_HUNT_GROUP,
      NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY:
        process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY,
      NEXT_PUBLIC_SUPABASE_URL: process.env.NEXT_PUBLIC_SUPABASE_URL,
      NEXT_PUBLIC_TURNSTILE_SITE_KEY:
        process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY,
      NODE_ENV: process.env.NODE_ENV,
      NOVU_DASHBOARD_URL: process.env.NOVU_DASHBOARD_URL,
      NOVU_SECRET_KEY: process.env.NOVU_SECRET_KEY,
      PDF_RENDER_MODE: process.env.PDF_RENDER_MODE,
      PORT: process.env.PORT,
      SEND_EMAIL_HOOK_SECRET: process.env.SEND_EMAIL_HOOK_SECRET,
      SENDGRID_API_KEY: process.env.SENDGRID_API_KEY,
      SIGNABLE_API_KEY: process.env.SIGNABLE_API_KEY,
      SIGNABLE_CONTRACT_SIGNING_ENABLED:
        process.env.SIGNABLE_CONTRACT_SIGNING_ENABLED,
      SIGNABLE_LOA_TEMPLATE_FINGERPRINT:
        process.env.SIGNABLE_LOA_TEMPLATE_FINGERPRINT,
      SIGNABLE_WATT_SENDS_CONTRACT_SIGNING_EMAIL:
        process.env.SIGNABLE_WATT_SENDS_CONTRACT_SIGNING_EMAIL,
      SIGNABLE_WATT_SENDS_LOA_SIGNING_EMAIL:
        process.env.SIGNABLE_WATT_SENDS_LOA_SIGNING_EMAIL,
      SUPABASE_SECRET_KEY: process.env.SUPABASE_SECRET_KEY,
      TABLE_CURSOR_SECRET: process.env.TABLE_CURSOR_SECRET,
      TABLE_EXPORT_DAILY_QUOTA: process.env.TABLE_EXPORT_DAILY_QUOTA,
      TABLE_EXPORT_MAX_ROWS: process.env.TABLE_EXPORT_MAX_ROWS,
      TABLE_RATE_LIMIT_GLOBAL_PER_MIN:
        process.env.TABLE_RATE_LIMIT_GLOBAL_PER_MIN,
      TABLE_RATE_LIMIT_LIST_PER_MIN: process.env.TABLE_RATE_LIMIT_LIST_PER_MIN,
      TABLE_RATE_LIMIT_MODE: process.env.TABLE_RATE_LIMIT_MODE,
      TEST_EMAIL: process.env.TEST_EMAIL,
      TURNSTILE_HOSTNAMES: process.env.TURNSTILE_HOSTNAMES,
      TURNSTILE_SECRET: process.env.TURNSTILE_SECRET,
      UDCORE_API_KEY: process.env.UDCORE_API_KEY,
      UDCORE_USER: process.env.UDCORE_USER,
      UPSTASH_REDIS_REST_TOKEN: process.env.UPSTASH_REDIS_REST_TOKEN,
      UPSTASH_REDIS_REST_URL: process.env.UPSTASH_REDIS_REST_URL,
      VERCEL_URL: process.env.VERCEL_URL
    },
    /**
     * Specify your server-side environment variables schema here. This way you can ensure the app isn't
     * built with invalid env vars.
     */
    server: {
      APERTURE_API_KEY: z.string().min(1),
      BRITISH_GAS_CLIENT_ID: z.string().min(1),
      COMPANIES_HOUSE_PRIVATE_API_KEY: z.string().min(1),
      CRM_PRIVATE_API_KEY: z.string().min(1),
      DATA8_PRIVATE_API_KEY: z.string().min(1),
      DATABASE_URL: z.string().url().min(1),
      /** Comma-separated recipients that receive every non-production email. Defaults to the Watt dev watcher list. */
      DEV_EMAIL_WATCHERS: z.string().optional(),
      DIRECT_URL: z.string().url().min(1),
      ELECTRALINKS_API_KEY: z.string().min(1),
      ELECTRALINKS_API_PASSWORD: z.string().min(1),
      ELECTRALINKS_HH_API_KEY: z.string().min(1),
      ELECTRALINKS_HH_API_PASSWORD: z.string().min(1),
      ENABLE_SOURCE_MAPS: z
        .literal("true")
        .or(z.literal("false"))
        .default("false"),
      EXPERIAN_CLIENT_ID: z.string().min(1),
      EXPERIAN_CLIENT_SECRET: z.string().min(1),
      EXPERIAN_PASSWORD: z.string().min(1),
      EXPERIAN_USERNAME: z.string().min(1),
      INNGEST_EVENT_KEY: z.string().min(1),
      INNGEST_SIGNING_KEY: z.string().min(1),
      INTERNAL_RECIPIENT_EMAIL: z.string().email().min(1),
      LEGACY_MYSQL_URL: z.string().url().min(1),
      NOVU_DASHBOARD_URL: z.string().url().min(1),
      NOVU_SECRET_KEY: z.string().min(1),
      /** Quote PDF strategy: `html` renders in-process via setContent (default); `url` navigates to /customer/quotes (legacy rollback path). */
      PDF_RENDER_MODE: z.enum(["html", "url"]).default("html"),
      SEND_EMAIL_HOOK_SECRET: z.string().min(1),
      SENDGRID_API_KEY: z.string().min(1),
      SIGNABLE_API_KEY: z.string().min(1),
      /** When true, written contracts use Signable for providers with a Signable template fingerprint configured. */
      SIGNABLE_CONTRACT_SIGNING_ENABLED: z
        .literal("true")
        .or(z.literal("false"))
        .default("false"),
      SIGNABLE_LOA_TEMPLATE_FINGERPRINT: z.string().min(1),
      /** When true, Watt emails the contract signing link. Disable Signer notification emails in Signable Preferences to avoid duplicate customer emails. */
      SIGNABLE_WATT_SENDS_CONTRACT_SIGNING_EMAIL: z
        .literal("true")
        .or(z.literal("false"))
        .default("true"),
      /** When true, Watt emails the signing link. Disable Signer notification emails in Signable Preferences to avoid duplicate customer emails. */
      SIGNABLE_WATT_SENDS_LOA_SIGNING_EMAIL: z
        .literal("true")
        .or(z.literal("false"))
        .default("true"),
      SUPABASE_SECRET_KEY: z.string().min(1),
      /** HMAC key for signed table cursors. */
      TABLE_CURSOR_SECRET: z.string().min(32),
      /** Maximum rows written by a server-side table export. */
      TABLE_EXPORT_DAILY_QUOTA: z.coerce.number().int().default(10),
      TABLE_EXPORT_MAX_ROWS: z.coerce.number().int().default(5000),
      TABLE_RATE_LIMIT_GLOBAL_PER_MIN: z.coerce
        .number()
        .int()
        .min(1)
        .default(600),
      TABLE_RATE_LIMIT_LIST_PER_MIN: z.coerce
        .number()
        .int()
        .min(1)
        .default(120),
      TABLE_RATE_LIMIT_MODE: z.enum(["log", "enforce"]).default("log"),
      TEST_EMAIL: z.literal("true").or(z.literal("false")).default("false"),
      /**
       * Comma-separated frontend hostnames allowed by Turnstile siteverify.
       * Local deployments: localhost,127.0.0.1. Production must omit localhost.
       */
      TURNSTILE_HOSTNAMES: z.string().min(1),
      /** Cloudflare Turnstile widget secret (server-only). */
      TURNSTILE_SECRET: z.string().min(1),
      UDCORE_API_KEY: z.string().min(1),
      UDCORE_USER: z.string().min(1),
      UPSTASH_REDIS_REST_TOKEN: z.string().min(1),
      UPSTASH_REDIS_REST_URL: z.string().url().min(1)
    },
    shared: {
      ENABLE_STAGEWISE_TOOLBAR: z
        .literal("true")
        .or(z.literal("false"))
        .default("false"),
      NEXT_PUBLIC_DISABLE_POSTHOG: z
        .literal("true")
        .or(z.literal("false"))
        .default("false"),
      NODE_ENV: z
        .enum(["development", "production", "test"])
        .default("development"),
      PORT: z.coerce.number().default(3000),
      VERCEL_URL: z
        .string()
        .optional()
        .transform(v => {
          if (!v) {
            return undefined;
          }
          return v.startsWith("https://") ? v : `https://${v}`;
        })
    }
  }) satisfies CreateEnvOptions;
